import { useQuery } from "@tanstack/react-query";
import { useDashboardDate } from "@/contexts/DashboardDateContext";
import { useDashboardDataSource } from "@/contexts/DashboardDataSourceContext";
import { DashboardWidget } from "../DashboardWidget";
import { getPlatformColor } from "../PlatformBadge";
import { TrendingUp, TrendingDown } from "lucide-react";

interface BlendedRoasCardProps {
  isEditMode: boolean;
  onRemove?: () => void;
  onResize?: () => void;
  onResetSize?: () => void;
}

export function BlendedRoasCard({ isEditMode, onRemove, onResize, onResetSize }: BlendedRoasCardProps) {
  const { startDate, endDate } = useDashboardDate();
  const { getUrl, dataKey, sourceType } = useDashboardDataSource();

  const { data: breakdown, isLoading, error, refetch } = useQuery({
    queryKey: ["blended-roas", dataKey, startDate, endDate],
    queryFn: async () => {
      const params = new URLSearchParams({ startDate, endDate });
      const res = await fetch(getUrl("platform-breakdown", params));
      if (!res.ok) throw new Error("Failed to fetch data");
      return res.json() as Promise<Array<{
        platformSlug: string;
        platformName: string;
        spend: number;
        conversionValue: number;
        roas: number;
      }>>;
    },
    enabled: sourceType === "unified",
  });

  const totalSpend = breakdown?.reduce((s, b) => s + b.spend, 0) || 0;
  const totalValue = breakdown?.reduce((s, b) => s + b.conversionValue, 0) || 0;
  const blendedRoas = totalSpend > 0 ? totalValue / totalSpend : 0;

  return (
    <DashboardWidget
      title="Blended ROAS"
      widgetType="kpi"
      isEditMode={isEditMode}
      isLoading={isLoading}
      error={error?.message ?? null}
      onRetry={() => refetch()}
      onRemove={onRemove}
      onResize={onResize}
      onResetSize={onResetSize}
      platformSlug="unified"
    >
      <div className="px-4 py-3 flex flex-col h-full" data-testid="card-blended-roas">
        <p className="text-2xl font-bold text-gray-900">
          {blendedRoas.toFixed(2)}x
        </p>
        <div className="flex items-center gap-1 mt-1">
          {blendedRoas >= 2 ? (
            <TrendingUp className="h-3.5 w-3.5 text-emerald-600" />
          ) : (
            <TrendingDown className="h-3.5 w-3.5 text-red-500" />
          )}
          <span className="text-xs text-gray-500">
            ${totalSpend.toLocaleString(undefined, { maximumFractionDigits: 0 })} spend
          </span>
        </div>

        <div className="mt-3 space-y-2 flex-1">
          {breakdown?.filter(b => b.spend > 0).map(b => {
            const pct = totalSpend > 0 ? (b.spend / totalSpend) * 100 : 0;
            return (
              <div key={b.platformSlug} className="flex items-center gap-2">
                <span
                  className="w-2 h-2 rounded-full flex-shrink-0"
                  style={{ backgroundColor: getPlatformColor(b.platformSlug) }}
                />
                <span className="text-xs text-gray-600 flex-1 truncate">{b.platformName}</span>
                <span className="text-xs font-medium text-gray-900">{b.roas.toFixed(2)}x</span>
                <span className="text-[10px] text-gray-400">{pct.toFixed(0)}%</span>
              </div>
            );
          })}
        </div>
      </div>
    </DashboardWidget>
  );
}
